import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from './supabase';
import { useSession } from './SessionContext';
import type { Project, Category, ProjectKeyword, ProjectCategoryInterest } from './database.client.types';
import { activateDiscoveryProject } from './discoveryProjectService';
import {
    getActivationRecoveryReason,
    shouldRecoverProjectActivation,
} from './projectActivationRecovery';
import {
    filterVisibleUserProjects,
    upsertVisibleUserProject,
} from './userProjectVisibility';
import { withTimeout } from './asyncTimeout';
import { msg } from '@lingui/core/macro';
import type { LocalizedMessage } from './i18n/messages';

const PROJECTS_REQUEST_TIMEOUT_MS = 15000;

export interface ProjectWithRelations extends Project {
    project_category_interests?: Array<ProjectCategoryInterest & {
        category?: Category;
    }>;
    project_keywords?: ProjectKeyword[];
}

interface UseUserProjectsReturn {
    projects: ProjectWithRelations[];
    isLoading: boolean;
    error: LocalizedMessage | null;
    refetch: () => Promise<void>;
}

interface UseUserProjectsOptions {
    enabled?: boolean;
}

export function useUserProjects(options: UseUserProjectsOptions = {}): UseUserProjectsReturn {
    const [projects, setProjects] = useState<ProjectWithRelations[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<LocalizedMessage | null>(null);
    const { session } = useSession();
    const activationRecoveryInFlightRef = useRef<Set<string>>(new Set());
    const enabled = options.enabled ?? true;

    const fetchProjects = useCallback(async () => {
        if (!enabled) {
            setProjects([]);
            setIsLoading(false);
            setError(null);
            return;
        }

        if (!session?.user?.id) {
            setProjects([]);
            setIsLoading(false);
            setError(null);
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            console.log('📋 [useUserProjects] Fetching projects for user:', session.user.id);

            const { data, error: fetchError } = await withTimeout(
                supabase
                    .from('project')
                    .select(`
                        *,
                        project_category_interests (
                            *,
                            category (*)
                        ),
                        project_keywords:project_keyword (*)
                    `)
                    .eq('user_id', session.user.id)
                    .eq('is_inbox', false)
                    .is('deleted_at', null)
                    .order('created_at', { ascending: false }),
                PROJECTS_REQUEST_TIMEOUT_MS,
                'Loading projects timed out. Please try again.'
            );

            if (fetchError) {
                console.error('❌ [useUserProjects] Error fetching projects:', fetchError);
                throw new Error(fetchError.message || 'Failed to load projects');
            }

            console.log('✅ [useUserProjects] Projects fetched successfully:', data?.length || 0);
            setProjects(filterVisibleUserProjects((data || []) as ProjectWithRelations[]));
            setError(null);
        } catch (err: any) {
            console.error('❌ [useUserProjects] Unexpected error:', err);
            setError(msg({ id: 'projects.loadFailure', message: 'Failed to load projects' }));
            setProjects([]);
        } finally {
            setIsLoading(false);
        }
    }, [enabled, session?.user?.id]);

    // Helper function to fetch a single project with all relations
    const fetchSingleProject = useCallback(async (projectId: string): Promise<ProjectWithRelations | null> => {
        try {
            const { data, error: fetchError } = await supabase
                .from('project')
                .select(`
                    *,
                    project_category_interests (
                        *,
                        category (*)
                    ),
                    project_keywords:project_keyword (*)
                `)
                .eq('id', projectId)
                .eq('is_inbox', false)
                .is('deleted_at', null)
                .maybeSingle();

            if (fetchError) {
                console.error('❌ [useUserProjects] Error fetching single project:', fetchError);
                return null;
            }

            return data as ProjectWithRelations | null;
        } catch (err: any) {
            console.error('❌ [useUserProjects] Unexpected error fetching single project:', err);
            return null;
        }
    }, []);

    // Fetch projects on mount and when session changes
    useEffect(() => {
        fetchProjects();
    }, [fetchProjects]);

    // Self-heal: if an authenticated user has eligible experimental projects,
    // promote them in the background and refresh the list.
    useEffect(() => {
        if (!session?.user?.email || projects.length === 0) {
            return;
        }

        const recoverableProjects = projects.filter((project) =>
            shouldRecoverProjectActivation(project, session)
        );

        if (recoverableProjects.length === 0) {
            return;
        }

        let cancelled = false;

        const recover = async () => {
            let activatedAny = false;

            for (const project of recoverableProjects) {
                if (activationRecoveryInFlightRef.current.has(project.id)) {
                    continue;
                }

                const reason = getActivationRecoveryReason(project, session);
                if (!reason) {
                    continue;
                }

                activationRecoveryInFlightRef.current.add(project.id);
                console.log(`🚀 [useUserProjects] Auto-activating ${project.id} (${reason})...`);

                try {
                    await activateDiscoveryProject(project.id);
                    activatedAny = true;
                    console.log(`✅ [useUserProjects] Auto-activated ${project.id}`);
                } catch (activationError) {
                    console.error(`❌ [useUserProjects] Auto-activation failed for ${project.id}:`, activationError);
                } finally {
                    activationRecoveryInFlightRef.current.delete(project.id);
                }
            }

            if (!cancelled && activatedAny) {
                await fetchProjects();
            }
        };

        void recover();

        return () => {
            cancelled = true;
        };
    }, [projects, session, fetchProjects]);

    // Set up real-time subscription for project updates with optimistic UI updates
    useEffect(() => {
        if (!enabled || !session?.user?.id) return;

        console.log('🔄 [useUserProjects] Setting up real-time subscription');

        const subscription = supabase
            .channel(`user-projects-${session.user.id}`)
            .on(
                'postgres_changes',
                {
                    event: 'INSERT',
                    schema: 'public',
                    table: 'project',
                    filter: `user_id=eq.${session.user.id}`
                },
                async (payload: any) => {
                    console.log('➕ [useUserProjects] New project inserted:', payload.new.id);

                    // Fetch the new project with all relations
                    const newProject = await fetchSingleProject(payload.new.id);
                    if (newProject) {
                        setProjects(prev => upsertVisibleUserProject(prev, newProject));
                    }
                }
            )
            .on(
                'postgres_changes',
                {
                    event: 'UPDATE',
                    schema: 'public',
                    table: 'project',
                    filter: `user_id=eq.${session.user.id}`
                },
                async (payload: any) => {
                    console.log('✏️ [useUserProjects] Project updated:', payload.new.id);

                    // Fetch the updated project with all relations
                    const updatedProject = await fetchSingleProject(payload.new.id);
                    if (updatedProject) {
                        setProjects(prev => upsertVisibleUserProject(prev, updatedProject));
                    } else {
                        // Project was soft-deleted or no longer accessible, remove it from state
                        console.log('🗑️ [useUserProjects] Project soft-deleted or inaccessible:', payload.new.id);
                        setProjects(prev => prev.filter(p => p.id !== payload.new.id));
                    }
                }
            )
            .on(
                'postgres_changes',
                {
                    event: 'DELETE',
                    schema: 'public',
                    table: 'project',
                    filter: `user_id=eq.${session.user.id}`
                },
                (payload: any) => {
                    console.log('🗑️ [useUserProjects] Project deleted:', payload.old.id);

                    // Remove the deleted project from state
                    setProjects(prev => prev.filter(p => p.id !== payload.old.id));
                }
            )
            .subscribe();

        return () => {
            console.log('🔄 [useUserProjects] Cleaning up subscription');
            subscription.unsubscribe();
        };
    }, [enabled, session?.user?.id, fetchSingleProject]);

    return {
        projects,
        isLoading,
        error,
        refetch: fetchProjects
    };
}
